import { Box, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Typography, Pagination, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import { getUrl } from "src/Utils/Common";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "1px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "1px",
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    height: 15,
  }));

function PromotionTable2({
  manufacturer
}) {

  const projectId = useSelector((state) => state.userProject.projectId);
  const[data, setData] = useState([]);
  const[loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const formatNumber = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }
    return num;
  };

  const formatNumberNoDecimals = (num) => {
    if (typeof num === 'number' && !isNaN(num)) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    return num;
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value - 1);
  };
    
  useEffect(()=>{
    if (!manufacturer) {
      console.warn("[PromotionTable2] Missing required props", { manufacturer });
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const normalizedManufacturer = manufacturer === "SAVOLA" ? "SAVOLA GROUP" : 
                                   manufacturer === "IFCO" ? "IFFCO GROUP" : manufacturer;
    const filtersJSON = encodeURIComponent(JSON.stringify({ manufacturer: normalizedManufacturer }));
    const url = `${getUrl()}/strategy_creation/uplift_distribution?project_id=${projectId}&filters=${filtersJSON}`;
    console.log("[PromotionTable2] Fetch URL:", url);
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        "x-access-token": localStorage.getItem("userId") || "",
      },
    })
      .then((res) => res.text())
      .then((text) => {
        try {
          const cleaned = text.replace(/\bNaN\b/g, "null");
          if (!cleaned.trim().startsWith("{") && !cleaned.trim().startsWith("[")) {
            console.error("Uplift Distribution API did not return JSON:", cleaned);
            setData([]);
            return;
          }
          const json = JSON.parse(cleaned);
          console.log("[PromotionTable2] API response:", json);
          
          const rows = [];
          const to2 = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
          };
          
          const dateArray = json.date || [];
          const promoOfferCodeArray = json.promo_offer_code || [];
          const minArray = json.min || [];
          const meanArray = json.mean || [];
          const maxArray = json.max || [];
          const promoCountArray = json.promo_count || [];
          const productivityArray = json.productivity || [];
          const avgProductivityArray = json.average_productivity || [];
          
          for (let i = 0; i < dateArray.length; i++) {
            if (dateArray[i] === 2024) {
            const item = {
                date: dateArray[i],
                promo_offer_code: promoOfferCodeArray[i],
                min: to2(minArray[i]),
                mean: to2(meanArray[i]),
                max: to2(maxArray[i]),
                promo_count: promoCountArray[i],
                productivity: productivityArray[i],
                avg_productivity: avgProductivityArray[i],
            };
            rows.push(item);
            }
          }
          
          console.log("[PromotionTable2] Rows built:", rows.length);
    setData(rows);
        } catch (e) {
          console.error("Uplift Distribution API parse error:", e);
          setData([]);
        }
      })
      .catch((err) => {
        console.error("PromotionTable2 fetch error:", err);
        setData([]);
      })
      .finally(() => setLoading(false));
  },
[manufacturer, projectId]
)

    return (
      <Box sx={{ mb: 6 }}>
        <Typography textAlign="center">Uplift Distribution</Typography>
            {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 2 }}
          >
                <FacebookCircularProgress />
              </Box>
            ) : data.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 2 }}
          >
            <FacebookCircularProgress
              loading={false}
              hasData={false}
              message="No data for selected filters"
            />
              </Box>
            ) : (
            <TableContainer
                    sx={{
                      maxHeight: 600,
                      overflowY: "auto",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      touchAction: "manipulation",
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Table stickyHeader aria-label="customized table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                          <StyledTableCell>#</StyledTableCell>
                          <StyledTableCell>Date</StyledTableCell>
                          <StyledTableCell>Promo Offer Code</StyledTableCell>
                          <StyledTableCell>Min</StyledTableCell>
                          <StyledTableCell>Mean</StyledTableCell>
                          <StyledTableCell>Max</StyledTableCell>
                          <StyledTableCell>Promo Count</StyledTableCell>
                          <StyledTableCell>
                            <Tooltip title="Productivity is calculated based on the avg promo volume sold" arrow>
                              <span>Promo Productivity</span>
                            </Tooltip>
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
        
                      <TableBody>
                {data
                  .slice(
                    currentPage * rowsPerPage,
                    currentPage * rowsPerPage + rowsPerPage
                  )
                  .map((item, idx) => {
                          const cellSx = { fontWeight: "bold" };
        
                          return (
                            <React.Fragment key={idx}>
                              <StyledTableRow>
                                <StyledTableCell sx={cellSx}>
                            {currentPage * rowsPerPage + idx + 1}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                            {item.date}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                            {item.promo_offer_code}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.min)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                  {formatNumberNoDecimals(item.mean)}
                                </StyledTableCell>
                                <StyledTableCell sx={cellSx}>
                                 {formatNumberNoDecimals(item.max)}
                                      </StyledTableCell>
                                      <StyledTableCell sx={cellSx}>
                                      {item.promo_count}
                                      </StyledTableCell>
                                      <StyledTableCell sx={cellSx}>
                                  <ColoredBox
                              number={formatNumber(item.productivity)}
                                    Color={
                                item.productivity > item.avg_productivity
                                        ? "#0BB783"
                                  : item.productivity === item.avg_productivity
                                              ? "#0095FF"
                                              : "#B22234"
                                          }
                                        />
                                      </StyledTableCell>
                                    </StyledTableRow>
                            </React.Fragment>
                          );
                        })}
                {data.length === 0 && !loading && (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} align="center" sx={{ py: 2 }}>
                      No data available
                    </StyledTableCell>
                  </StyledTableRow>
                )}
                      </TableBody>
                    </Table>
                  </TableContainer>
            )}
      </Box>
    );
}

export default PromotionTable2

PromotionTable2.propTypes = {
  manufacturer: PropTypes.string.isRequired,
};